import React, { useState, useRef } from 'react';
import { Textarea } from '@/components/ui/Textarea/Textarea';
import { Button } from '@/components/ui/Button/Button';
import { feedbackService, type Attachment } from '@/services/feedbackService';
import { useNotificationStore } from '@/stores/notificationStore';

interface BugReportFormProps {
  onSuccess: () => void;
}

export function BugReportForm({ onSuccess }: BugReportFormProps) {
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const notify = useNotificationStore();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files) {
      setFiles((prev) => [...prev, ...Array.from(e.dataTransfer.files)]);
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) return;

    setIsSubmitting(true);
    try {
      const metadata = {
        userAgent: navigator.userAgent,
        pageUrl: window.location.href,
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight,
        timestamp: new Date().toISOString(),
      };

      const ticket = await feedbackService.createTicket({
        type: 'bug',
        subject: description.slice(0, 100),
        body: description,
        metadata,
      });

      if (files.length > 0) {
        const attachments: Attachment[] = [];
        for (const file of files) {
          try {
            const attachment = await feedbackService.uploadAttachment(ticket.id, file);
            attachments.push(attachment);
          } catch (err) {
            console.error('Failed to upload attachment:', err);
          }
        }
        if (attachments.length > 0) {
          await feedbackService.sendMessage(ticket.id, 'Attachments:', attachments);
        }
      }

      notify.success('Bug report submitted! Thank you for your feedback.');
      onSuccess();
    } catch (err: any) {
      notify.error(err.message || 'Failed to submit bug report');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Textarea
        label="What went wrong?"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Describe the bug you encountered. What were you trying to do? What happened instead?"
        rows={5}
        required
      />

      {/* File Attachment Area */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          Attachments (optional)
        </label>
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-input rounded-md p-4 text-center cursor-pointer hover:border-primary-400 hover:bg-primary-50/50 transition-colors"
        >
          <svg className="w-6 h-6 mx-auto text-muted-foreground mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <p className="text-sm text-muted-foreground">
            Drop files here or <span className="text-primary-600 font-medium">browse</span>
          </p>
          <p className="text-xs text-muted-foreground mt-1">Screenshots, logs, or any relevant files</p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileChange}
          className="hidden"
          accept="image/*,.pdf,.txt,.log,.json"
        />

        {files.length > 0 && (
          <div className="mt-2 space-y-1">
            {files.map((file, i) => (
              <div key={i} className="flex items-center justify-between bg-muted rounded-md px-3 py-1.5 text-sm">
                <span className="truncate text-foreground">{file.name}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFile(i)}
                  className="ml-2 p-1 h-auto text-muted-foreground hover:text-error-600"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end pt-2">
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={isSubmitting || !description.trim()}
          isLoading={isSubmitting}
        >
          Submit Bug Report
        </Button>
      </div>
    </form>
  );
}
